import React, { useCallback } from "react";

import { Link as RRLink } from "react-router-dom";
import { useForm } from "react-hook-form-7";

import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Link,
  Stack,
  StackDivider,
  Text,
} from "@chakra-ui/react";

import { useSendConnectionLink } from "../../../graphql/generated/definitions";

import useTranslations from "../../../hooks/useTranslations";

import LoginLayout from "../layout/LoginLayout";
import NavigationTabs from "../components/NavigationTabs";

type ConnectionLinkFormInputs = { email: string };

export default function ConnectionLinkPage() {
  const t = useTranslations();

  const {
    register,
    formState: { errors, isSubmitSuccessful, isValid },
    handleSubmit,
  } = useForm<ConnectionLinkFormInputs>({ mode: "onChange" });

  const [sendConnectionLink] = useSendConnectionLink();

  const onSubmit = useCallback(async ({ email }: ConnectionLinkFormInputs) => {
    try {
      await sendConnectionLink({
        variables: {
          input: { email },
        },
      });
    } catch {
      // Should not append
    }
  }, []);

  return (
    <LoginLayout title={t("login.gladToSeeYou") as string}>
      <Stack divider={<StackDivider color="gray.200" />} spacing="6">
        <NavigationTabs />
        <Stack spacing="10">
          <Stack>
            <Text fontWeight="semibold">
              {t("connectionLink.accessThroughConnectionLink")}
            </Text>
            <Text>
              {t("connectionLink.accessThroughConnectionLinkDescription")}
            </Text>
          </Stack>
          <form onSubmit={handleSubmit(onSubmit)} id="connection-link-form">
            <Stack spacing="5">
              <FormControl
                id="email"
                isRequired
                isInvalid={!!errors?.email?.message}
              >
                <FormLabel>{t("forms.fields.email")}</FormLabel>
                <Input
                  size="lg"
                  type="email"
                  // This looks rather ugly but native form validation block the one made by RHF
                  isRequired={false}
                  aria-required="true"
                  {...register("email", {
                    required: t("forms.errors.required") as string,
                  })}
                />
                <FormErrorMessage>{errors?.email?.message}</FormErrorMessage>
              </FormControl>
              {isSubmitSuccessful && (
                <Alert
                  status="success"
                  flexDirection="column"
                  alignItems="flex-start"
                  justifyContent="flex-start"
                  id="connection-link-form-success"
                >
                  <AlertTitle>{t("connectionLink.emailSentLabel")}</AlertTitle>
                  <AlertDescription>
                    {t("connectionLink.emailSentDescription")}
                  </AlertDescription>
                </Alert>
              )}
              <Stack spacing="6" pt="4">
                <Button
                  disabled={!isValid}
                  type="submit"
                  isFullWidth
                  colorScheme="primary"
                >
                  {t("connectionLink.connectionLink")}
                </Button>
                <Link
                  as={RRLink}
                  to="/login"
                  color="secondary"
                  textAlign="center"
                  fontSize="sm"
                  fontWeight="semibold"
                >
                  {t("login.backToLogin")}
                </Link>
              </Stack>
            </Stack>
          </form>
        </Stack>
      </Stack>
    </LoginLayout>
  );
}
